/**
 * Format storage values for Infralytiqs panels.
 *
 * - `decimal_mb`: metric is already decimal MB in ClickHouse (1 GB → 1000 MB stored).
 * - `bytes` + `decimal`: raw byte counts with decimal/SI display divisors.
 */
export type ByteUnitSystem = 'decimal' | 'binary';

/** Values already stored as decimal megabytes (AEM normalised ingest). */
export const formatDecimalMegabytes = (mb: number): string => {
  if (!Number.isFinite(mb)) return '0 MB';
  const abs = Math.abs(mb);
  const sign = mb < 0 ? '-' : '';
  if (abs >= 1_000_000_000) {
    const pb = abs / 1_000_000_000;
    return `${sign}${pb >= 100 ? pb.toFixed(0) : pb >= 10 ? pb.toFixed(1) : pb.toFixed(2)} PB`;
  }
  if (abs >= 1_000_000) {
    const tb = abs / 1_000_000;
    return `${sign}${tb >= 100 ? tb.toFixed(0) : tb >= 10 ? tb.toFixed(1) : tb.toFixed(2)} TB`;
  }
  if (abs >= 1000) {
    const gb = abs / 1000;
    return `${sign}${gb >= 100 ? gb.toFixed(0) : gb >= 10 ? gb.toFixed(1) : gb.toFixed(2)} GB`;
  }
  return `${sign}${abs >= 100 ? abs.toFixed(0) : abs.toFixed(1)} MB`;
};

const DECIMAL_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
const BINARY_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'] as const;

export const formatStorageBytes = (
  bytes: number,
  opts?: { fixedUnit?: 'GB' | 'TB'; system?: ByteUnitSystem },
): string => {
  if (!Number.isFinite(bytes)) return '0 B';
  const system = opts?.system ?? 'decimal';
  const base = system === 'decimal' ? 1000 : 1024;
  const abs = Math.abs(bytes);
  const sign = bytes < 0 ? '-' : '';

  if (opts?.fixedUnit === 'TB') {
    const tb = abs / base ** 4;
    if (tb >= 100) return `${sign}${tb.toFixed(0)} TB`;
    if (tb >= 10) return `${sign}${tb.toFixed(1)} TB`;
    return `${sign}${tb.toFixed(2)} TB`;
  }
  if (opts?.fixedUnit === 'GB') {
    const gb = abs / base ** 3;
    if (gb >= 100) return `${sign}${gb.toFixed(0)} GB`;
    if (gb >= 10) return `${sign}${gb.toFixed(1)} GB`;
    return `${sign}${gb.toFixed(2)} GB`;
  }

  const units = system === 'decimal' ? DECIMAL_UNITS : BINARY_UNITS;
  if (abs < base) return `${sign}${abs.toFixed(0)} ${units[0]}`;
  const tier = Math.min(Math.floor(Math.log(abs) / Math.log(base)), units.length - 1);
  const scaled = abs / base ** tier;
  const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
  return `${sign}${scaled.toFixed(decimals)} ${units[tier]}`;
};
